"use client"

import { useState, type ReactNode } from "react"

import {
  Box,
  Heading,
  Text,
  Badge,
  HStack,
  Button,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  useToast,
  Card,
  CardBody,
  Spinner,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepStatus,
  StepTitle,
  Stepper,
  Progress,
  useColorModeValue,
  VStack,
  Tooltip,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  StepSeparator,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react"
import {
  FaDownload,
  FaSync,
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle,
  FaClipboardCheck,
  FaFileExcel,
} from "react-icons/fa"
import * as XLSX from "xlsx"

export type ImportStep = {
  title: string
  description: string
}

export type ImportLog = {
  message: string
  type: "info" | "success" | "warning" | "error"
  rowData?: any // Dados da linha que causou o erro
  rowIndex?: number // Índice da linha que causou o erro
}

export type ImportStats = {
  [key: string]: number
}

export type ProcessingFunction<T> = (
  data: T,
  addLog: (message: string, type: ImportLog["type"], rowData?: any, rowIndex?: number) => void,
  updateStats: (key: string, increment?: number) => void,
  updateProgress: (sheetName: string, count: number) => void,
  setCurrentStep: (step: number) => void,
) => Promise<void>

export type TabData = {
  id: string
  title: ReactNode
  content: ReactNode
  metadata?: {
    totalRows?: number
    processedRows?: number
  }
}

interface ImportProcessorProps<T> {
  title: string
  subtitle?: string
  data: T
  steps: ImportStep[]
  tabs: TabData[]
  processFunction: ProcessingFunction<T>
  initialStats?: ImportStats
  actionButtonText?: string
  showDownloadButton?: boolean
  onDownload?: (tabId: string) => void
  onClose?: () => void
  isOpen?: boolean
}

export default function ImportProcessor<T>({
  title,
  subtitle,
  data,
  steps,
  tabs,
  processFunction,
  initialStats = {},
  actionButtonText = "Processar Dados",
  showDownloadButton = false,
  onDownload,
  onClose,
  isOpen = false,
}: ImportProcessorProps<T>) {
  const toast = useToast()
  const [loadingTabs, setLoadingTabs] = useState<Record<string, boolean>>({})
  const [processedItems, setProcessedItems] = useState<Record<string, number>>({})
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [currentTabIndex, setCurrentTabIndex] = useState(0)
  const [processingLogs, setProcessingLogs] = useState<ImportLog[]>([])
  const [errorLogs, setErrorLogs] = useState<ImportLog[]>([])
  const [errorData, setErrorData] = useState<any[]>([])
  const [showProcessingSummary, setShowProcessingSummary] = useState(false)
  const [processingStats, setProcessingStats] = useState<ImportStats>(initialStats)

  const bgColor = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const highlightColor = useColorModeValue("blue.50", "blue.900")

  const addProcessingLog = (message: string, type: ImportLog["type"], rowData?: any, rowIndex?: number) => {
    const logEntry: ImportLog = { message, type, rowData, rowIndex }
    setProcessingLogs((prev) => [...prev, logEntry])

    // Se for um erro, adicionar também ao log de erros
    if (type === "error") {
      setErrorLogs((prev) => [...prev, logEntry])

      // Se tiver dados da linha, adicionar aos dados de erro
      if (rowData) {
        setErrorData((prev) => [...prev, { ...rowData, _errorMessage: message, _rowIndex: rowIndex }])
      }
    }
  }

  const updateStats = (key: string, increment = 1) => {
    setProcessingStats((prev) => ({
      ...prev,
      [key]: (prev[key] || 0) + increment,
    }))
  }

  const updateProgress = (tabId: string, count: number) => {
    setProcessedItems((prev) => ({ ...prev, [tabId]: count }))
  }

  const downloadErrorsAsExcel = () => {
    if (errorData.length === 0) {
      toast({
        title: "Nenhum erro encontrado",
        description: "Não há dados com erro para download.",
        status: "info",
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      // Criar uma nova planilha com os dados de erro
      const worksheet = XLSX.utils.json_to_sheet(errorData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, "Erros")

      // Gerar o arquivo e fazer download
      const fileName = `erros_importacao_${new Date().toISOString().split("T")[0]}.xlsx`
      XLSX.writeFile(workbook, fileName)

      toast({
        title: "Download concluído",
        description: `Arquivo ${fileName} baixado com sucesso.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: "Erro no download",
        description: "Não foi possível gerar o arquivo Excel.",
        status: "error",
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const startProcessing = async () => {
    setIsProcessing(true)
    setCurrentStep(0)
    setProcessingLogs([])
    setErrorLogs([])
    setErrorData([])
    setShowProcessingSummary(false)
    setProcessingStats(initialStats)

    try {
      const loadingState: Record<string, boolean> = {}
      tabs.forEach((tab) => {
        loadingState[tab.id] = true
      })
      setLoadingTabs(loadingState)

      await processFunction(data, addProcessingLog, updateStats, updateProgress, setCurrentStep)

      setShowProcessingSummary(true)
      toast({
        title: "Processamento concluído",
        description: `Todos os itens foram processados.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      })
    } catch (error) {
      addProcessingLog("Erro durante o processamento", "error")
      updateStats("errors", 1)
      toast({
        title: "Erro durante o processamento",
        description: "Ocorreu um erro inesperado",
        status: "error",
        duration: 5000,
        isClosable: true,
      })
    } finally {
      const loadingState: Record<string, boolean> = {}
      tabs.forEach((tab) => {
        loadingState[tab.id] = false
      })
      setLoadingTabs(loadingState)
      setIsProcessing(false)
    }
  }

  const content = (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap">
        <Box mb={[4, 0]}>
          <Heading as="h2" size="lg" mb={2}>
            {title}
            {subtitle && (
              <Text as="span" color="blue.500" ml={2}>
                {subtitle}
              </Text>
            )}
          </Heading>
          <HStack spacing={2}>
            <Badge colorScheme="green">{tabs.length} itens encontrados</Badge>
            {Object.keys(processedItems).length > 0 && (
              <Badge colorScheme="blue">
                {Object.values(processedItems).reduce((sum, count) => sum + count, 0)} itens processados
              </Badge>
            )}
          </HStack>
        </Box>

        <Button
          leftIcon={<Icon as={isProcessing ? Spinner : FaSync} />}
          colorScheme="blue"
          variant="solid"
          size="md"
          onClick={startProcessing}
          isLoading={isProcessing}
          loadingText="Processando..."
          disabled={isProcessing}
        >
          {actionButtonText}
        </Button>
      </Flex>

      {isProcessing && (
        <Card mb={6} borderColor={borderColor} boxShadow="md">
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Flex justify="space-between" align="center">
                <Heading size="md">Status do Processamento</Heading>
                <HStack>
                  <Badge colorScheme="blue">
                    Item: {currentTabIndex + 1} de {tabs.length}
                  </Badge>
                  <Badge colorScheme="green">
                    Etapa: {currentStep + 1} de {steps.length}
                  </Badge>
                </HStack>
              </Flex>

              <Stepper index={currentStep} colorScheme="blue" size="sm">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                    </StepIndicator>
                    <Box flexShrink="0">
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>
                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>

              <Box>
                <Text mb={2}>Progresso do item atual: {tabs[currentTabIndex]?.title}</Text>
                <Progress
                  hasStripe
                  isAnimated
                  value={processedItems[tabs[currentTabIndex]?.id] || 0}
                  max={tabs[currentTabIndex]?.metadata?.totalRows || 100}
                  colorScheme="blue"
                  borderRadius="md"
                />
              </Box>

              <Box
                maxH="200px"
                overflowY="auto"
                p={3}
                borderRadius="md"
                bg="gray.50"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Heading size="xs" mb={2}>
                  Log de Processamento
                </Heading>
                {processingLogs.slice(-10).map((log, index) => (
                  <Flex key={index} mb={1} align="center">
                    <Icon
                      as={
                        log.type === "success"
                          ? FaCheck
                          : log.type === "warning"
                            ? FaExclamationTriangle
                            : log.type === "error"
                              ? FaExclamationTriangle
                              : FaInfoCircle
                      }
                      color={
                        log.type === "success"
                          ? "green.500"
                          : log.type === "warning"
                            ? "orange.500"
                            : log.type === "error"
                              ? "red.500"
                              : "blue.500"
                      }
                      mr={2}
                    />
                    <Text
                      fontSize="sm"
                      color={
                        log.type === "success"
                          ? "green.700"
                          : log.type === "warning"
                            ? "orange.700"
                            : log.type === "error"
                              ? "red.700"
                              : "gray.700"
                      }
                    >
                      {log.message}
                    </Text>
                  </Flex>
                ))}
              </Box>
            </VStack>
          </CardBody>
        </Card>
      )}

      {showProcessingSummary && (
        <Card mb={6} borderColor={borderColor} boxShadow="md" bg={highlightColor}>
          <CardBody>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Resumo do Processamento</Heading>
              <IconButton
                aria-label="Fechar resumo"
                icon={<CloseButton />}
                size="sm"
                onClick={() => setShowProcessingSummary(false)}
              />
            </Flex>
            <StatGroup>
              {Object.entries(processingStats).map(([key, value]) => (
                <Stat key={key} justifyContent={"center"} textAlign={"center"} justifyItems={"center"}>
                  <StatLabel>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1")}</StatLabel>
                  <StatNumber color={key === "errors" && value > 0 ? "red.500" : undefined}>{value}</StatNumber>
                </Stat>
              ))}
            </StatGroup>
            {processingStats.errors > 0 && (
              <Alert status="warning" mt={4}>
                <AlertIcon />
                <AlertTitle>Atenção!</AlertTitle>
                <AlertDescription>
                  Foram encontrados {processingStats.errors} erros durante o processamento. Verifique o log para mais
                  detalhes.
                </AlertDescription>
              </Alert>
            )}
          </CardBody>
        </Card>
      )}

      <Tabs variant="soft-rounded" colorScheme="blue" isFitted onChange={(index) => setCurrentTabIndex(index)}>
        <TabList overflowX="auto" whiteSpace="nowrap" pb={2} borderBottom="1px solid #e2e8f0">
          {tabs.map((tab) => (
            <Tab key={tab.id}>
              {tab.title}
              {processedItems[tab.id] > 0 && (
                <Badge ml={2} colorScheme="green">
                  {processedItems[tab.id]}
                </Badge>
              )}
              {loadingTabs[tab.id] && <Spinner size="xs" ml={2} />}
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab.id} p={0} pt={6}>
              <Card>
                <CardBody>
                  <Flex justify="space-between" align="center" mb={4} flexWrap="wrap">
                    <HStack spacing={2}>
                      <Text fontWeight="semibold" fontSize="lg">
                        {typeof tab.title === "string" ? tab.title : "Item"}
                      </Text>
                      {tab.metadata?.totalRows && <Badge colorScheme="purple">{tab.metadata.totalRows} linhas</Badge>}
                      {processedItems[tab.id] > 0 && (
                        <Badge colorScheme="green">{processedItems[tab.id]} processados</Badge>
                      )}
                    </HStack>

                    {showDownloadButton && (
                      <Tooltip label="Baixar dados">
                        <IconButton
                          aria-label="Baixar dados"
                          icon={<FaDownload />}
                          size="sm"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => onDownload && onDownload(tab.id)}
                        />
                      </Tooltip>
                    )}
                  </Flex>

                  {loadingTabs[tab.id] ? (
                    <Flex justify="center" py={10} direction="column" align="center">
                      <Spinner size="lg" mb={4} color="blue.500" />
                      <Text fontWeight="medium">Processando dados...</Text>
                      {processedItems[tab.id] > 0 && (
                        <Text mt={2} color="blue.500" fontWeight="bold">
                          {processedItems[tab.id]} itens processados
                        </Text>
                      )}
                    </Flex>
                  ) : (
                    tab.content
                  )}
                </CardBody>
              </Card>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {/* Log de Erros Separado */}
      {errorLogs.length > 0 && (
        <Card mt={6} borderColor="red.200" borderWidth="2px">
          <CardBody>
            <Flex justify="space-between" align="center" mb={4}>
              <HStack>
                <Heading size="md" color="red.600">
                  Log de Erros
                </Heading>
                <Badge colorScheme="red">{errorLogs.length} erros</Badge>
              </HStack>
              <HStack>
                <Button
                  size="sm"
                  leftIcon={<Icon as={FaFileExcel} />}
                  colorScheme="red"
                  variant="outline"
                  onClick={downloadErrorsAsExcel}
                  disabled={errorData.length === 0}
                >
                  Download Excel dos Erros
                </Button>
                <Button
                  size="sm"
                  leftIcon={<Icon as={FaClipboardCheck} />}
                  onClick={() => {
                    const errorLogText = errorLogs.map((log) => `[ERRO] ${log.message}`).join("\n")
                    navigator.clipboard.writeText(errorLogText)
                    toast({
                      title: "Log de erros copiado",
                      description: "O log de erros foi copiado para a área de transferência",
                      status: "success",
                      duration: 2000,
                    })
                  }}
                >
                  Copiar Erros
                </Button>
              </HStack>
            </Flex>

            <Box
              maxH="300px"
              overflowY="auto"
              p={3}
              borderRadius="md"
              bg="red.50"
              borderWidth="1px"
              borderColor="red.200"
            >
              {errorLogs.map((log, index) => (
                <Flex
                  key={index}
                  mb={2}
                  align="flex-start"
                  p={2}
                  bg="white"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="red.100"
                >
                  <Icon as={FaExclamationTriangle} color="red.500" mr={2} mt={0.5} />
                  <Box flex="1">
                    <Text fontSize="sm" color="red.700" fontWeight="medium">
                      {log.message}
                    </Text>
                    {log.rowIndex !== undefined && (
                      <Text fontSize="xs" color="red.500" mt={1}>
                        Linha: {log.rowIndex + 1}
                      </Text>
                    )}
                  </Box>
                </Flex>
              ))}
            </Box>
          </CardBody>
        </Card>
      )}

      {processingLogs.length > 0 && (
        <Card mt={6}>
          <CardBody>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Log Completo de Processamento</Heading>
              <Button
                size="sm"
                leftIcon={<Icon as={FaClipboardCheck} />}
                onClick={() => {
                  const logText = processingLogs.map((log) => `[${log.type.toUpperCase()}] ${log.message}`).join("\n")
                  navigator.clipboard.writeText(logText)
                  toast({
                    title: "Log copiado",
                    description: "O log de processamento foi copiado para a área de transferência",
                    status: "success",
                    duration: 2000,
                  })
                }}
              >
                Copiar Log
              </Button>
            </Flex>

            <Box
              maxH="300px"
              overflowY="auto"
              p={3}
              borderRadius="md"
              bg="gray.50"
              borderWidth="1px"
              borderColor={borderColor}
            >
              {processingLogs.map((log, index) => (
                <Flex key={index} mb={1} align="center">
                  <Icon
                    as={
                      log.type === "success"
                        ? FaCheck
                        : log.type === "warning"
                          ? FaExclamationTriangle
                          : log.type === "error"
                            ? FaExclamationTriangle
                            : FaInfoCircle
                    }
                    color={
                      log.type === "success"
                        ? "green.500"
                        : log.type === "warning"
                          ? "orange.500"
                          : log.type === "error"
                            ? "red.500"
                            : "blue.500"
                    }
                    mr={2}
                  />
                  <Text
                    fontSize="sm"
                    color={
                      log.type === "success"
                        ? "green.700"
                        : log.type === "warning"
                          ? "orange.700"
                          : log.type === "error"
                            ? "red.700"
                            : "gray.700"
                    }
                  >
                    {log.message}
                  </Text>
                </Flex>
              ))}
            </Box>
          </CardBody>
        </Card>
      )}
    </Box>
  )

  if (isOpen !== undefined) {
    return (
      <Modal isOpen={isOpen} onClose={onClose || (() => {})} size={"full"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>{content}</ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }

  return <>{content}</>
}
